/** The device buffer's encryption (decision 0051): AES-GCM with the recording's 32-byte key, a fresh 12-byte IV per
 * chunk stored in front of the ciphertext, and additional authenticated data "<recording_id>|<track>|<seq>", so a chunk
 * cannot be moved to another recording, track or position. The key lives in memory only. */
import type { Track } from "./types.js";

const IV_BYTES = 12;

export function aadFor(recordingId: string, track: Track, seq: number): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(`${recordingId}|${track}|${seq}`) as Uint8Array<ArrayBuffer>;
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function importKey(b64: string): Promise<CryptoKey> {
  const bytes = base64ToBytes(b64);
  if (bytes.length !== 32) throw new Error("The recording key is not 32 bytes.");
  // Not extractable: the key cannot be read back out of the CryptoKey object.
  return crypto.subtle.importKey("raw", bytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export async function encryptChunk(key: CryptoKey, aad: Uint8Array<ArrayBuffer>, plain: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad }, key, plain));
  const out = new Uint8Array(IV_BYTES + cipher.length);
  out.set(iv, 0);
  out.set(cipher, IV_BYTES);
  return out;
}

export async function decryptChunk(key: CryptoKey, aad: Uint8Array<ArrayBuffer>, data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  if (data.length <= IV_BYTES) throw new Error("The stored chunk is too short.");
  const iv = data.slice(0, IV_BYTES);
  const body = data.slice(IV_BYTES);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv, additionalData: aad }, key, body));
}
